// ── useModuleAccess — the ONE answer to "is this module on for me?" ──────────
//
// Every surface that can show a module (sidebar, dock, More sheet, command
// palette, dashboards, quick actions, notification links, route guard) asks
// this hook. There is no second source. See lib/module-access.ts for the
// vocabulary (visible / locked / hidden) and hooks/use-entitlements.ts for the
// query.
//
//   const { hasModule, visibility, moduleState } = useModuleAccess();
//   hasModule("transport")                   // boolean, true while loading
//   hasModule("library", child.schoolId)     // per-child school (parents)
//   visibility("exams") === "locked"         // admin roles: render the teaser
//
// `useModuleAccessSync()` is mounted once in the app shell. It keeps the
// entitlements fresh: window focus / tab visible, Capacitor app resume, and
// any API call that returns 403 MODULE_NOT_IN_PLAN. (The `entitlements:changed`
// socket event is handled with the rest of the socket fan-out in
// hooks/use-socket.ts.)

import { useEffect, useMemo } from "react";
import { skipToken, useQuery, useQueryClient } from "@tanstack/react-query";
import { ENTITLEMENTS_KEY, ENTITLEMENTS_STALE_MS, useAmbientQueryClient, useEntitlements } from "@/hooks/use-entitlements";
import { childrenQueryKey, useActiveChildStore, type ChildrenResponse } from "@/hooks/use-active-child";
import { createModuleAccess, type EntitlementsResponse, type ModuleAccess } from "@/lib/module-access";
import { onModuleNotInPlan } from "@/lib/module-access-events";
import { isNative } from "@/lib/platform";
import { useAuthStore } from "@/store/auth-store";

export interface UseModuleAccessResult extends ModuleAccess {
  /** The raw response, for the few callers (the teaser page) that print plan details. */
  data: EntitlementsResponse | undefined;
  isLoading: boolean;
}

export function useModuleAccess(): UseModuleAccessResult {
  const { data, isLoading } = useEntitlements();
  const client = useAmbientQueryClient();
  const activeSchoolId = useAuthStore((s) => s.activeSchoolId);
  const selectedChildId = useActiveChildStore((s) => s.selectedChildId);
  // A parent with children in TWO schools is judged per child: the active
  // child's school. Read from the roster cache without fetching it (the
  // dashboard's own `useChildren` fills it); nobody else has `schools[]`.
  const roster = useQuery(
    { queryKey: childrenQueryKey(activeSchoolId), queryFn: skipToken, staleTime: Infinity },
    client,
  ).data as ChildrenResponse | undefined;
  const childSchoolId = useMemo(() => {
    if (!data?.schools || data.schools.length < 2) return null;
    const kids = roster?.children ?? [];
    return (kids.find((c) => c.id === selectedChildId) ?? kids[0])?.schoolId ?? null;
  }, [data?.schools, roster, selectedChildId]);
  return useMemo(() => {
    const access = createModuleAccess(data, { defaultSchoolId: childSchoolId });
    return { ...access, data, isLoading: isLoading && !data };
  }, [data, isLoading, childSchoolId]);
}

/** Mount once (AppShell). Refetch triggers other than the socket. */
export function useModuleAccessSync(): void {
  const queryClient = useQueryClient();
  const isAuthed = useAuthStore((s) => Boolean(s.token));

  useEffect(() => {
    if (!isAuthed) return;

    const refetch = (): void => {
      void queryClient.invalidateQueries({ queryKey: ENTITLEMENTS_KEY });
    };
    // Focus and visibility both fire when a tab comes back; only refetch if
    // the copy is older than the freshness window so we do not double up.
    const refetchIfStale = (): void => {
      const state = queryClient.getQueryState(ENTITLEMENTS_KEY);
      if (!state || Date.now() - state.dataUpdatedAt >= ENTITLEMENTS_STALE_MS / 2) refetch();
    };
    const onVisible = (): void => {
      if (document.visibilityState === "visible") refetchIfStale();
    };

    window.addEventListener("focus", refetchIfStale);
    document.addEventListener("visibilitychange", onVisible);
    const offNotInPlan = onModuleNotInPlan(refetch);

    let removeResume: (() => void) | null = null;
    let cancelled = false;
    if (isNative()) {
      void import("@capacitor/app")
        .then(({ App }) =>
          App.addListener("appStateChange", ({ isActive }) => {
            if (isActive) refetch();
          }),
        )
        .then((handle) => {
          if (cancelled) void handle.remove();
          else removeResume = () => void handle.remove();
        })
        .catch(() => {
          /* the web focus listener still covers a resumed WebView */
        });
    }

    return () => {
      cancelled = true;
      window.removeEventListener("focus", refetchIfStale);
      document.removeEventListener("visibilitychange", onVisible);
      offNotInPlan();
      removeResume?.();
    };
  }, [isAuthed, queryClient]);
}
